import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useUserPermissions } from "../../../UserPermissionsContext";
import Cookies from "js-cookie";
import { all_routes } from "../../../router/all_routes";
import { useNavigate } from "react-router";
import { Spin } from "antd";

interface SalesTrendItem {
  label: string;
  total: number;
  scts: number;
  ets: number;
}

const formatAmount = (value: number) => {
  if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
};

const RevenueTrend = () => {
  const { userPermissions, webApi } = useUserPermissions();
  const jwtToken = Cookies.get("authToken");
  const navigate = useNavigate();
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] =
    useState<(typeof filters)[number]>("year");
  const filters = ["week", "month", "year"] as const;

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  //  Sales Trend Data
  const fetchRevenueTrendData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${webApi}/sed/get_revenue_graph/?filter=${selectedFilter}`,
        options,
      );
      const data = await response.json();
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.code === "token_not_valid") {
          navigate("/academic/sri-chaitanya/crm/landing-page");
        }
        throw new Error(
          errorData.detail || "Failed to fetch SED Revenue trend data",
        );
      }
      setData(data.results);
    } catch (error: any) {
      console.error("Sales trend error ", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenueTrendData();
  }, [selectedFilter]);

  if (loading) {
    <Spin />;
  }

  return (
    <div
      style={{
        width: "100%",
        height: 400,
        background: "#fff",
        borderRadius: 10,
        padding: 20,
        boxShadow: "0 2px 8px rgba(0,0,0,.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h3
          style={{
            margin: 0,
            color: "#1f2d5c",
            fontWeight: 600,
          }}
        >
          Revenue Trend
        </h3>

        <div
          style={{
            display: "flex",
            gap: 10,
          }}
        >
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setSelectedFilter(filter)}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border:
                  selectedFilter === filter
                    ? "1px solid #2563eb"
                    : "1px solid #d1d5db",
                background: selectedFilter === filter ? "#2563eb" : "#fff",
                color: selectedFilter === filter ? "#fff" : "#374151",
                cursor: "pointer",
                fontWeight: 500,
                textTransform: "capitalize",
                transition: "0.2s",
              }}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height="90%">
        <AreaChart
          data={data}
          margin={{
            top: 10,
            right: 30,
            left: 10,
            bottom: 0,
          }}
        >
          <defs>
            <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />
              <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" />

          <XAxis dataKey="label" tick={{ fontSize: 12 }} />

          <YAxis tickFormatter={formatAmount} />

          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload || !payload.length) return null;

              const { total, scts, ets } = payload[0].payload;

              return (
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    padding: 12,
                    boxShadow: "0 2px 8px rgba(0,0,0,.15)",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 600,
                      marginBottom: 8,
                    }}
                  >
                    {label}
                  </p>

                  <p style={{ margin: "4px 0", color: "#2563eb" }}>
                    <strong>Total:</strong> ₹{total.toLocaleString()}
                  </p>

                  <p style={{ margin: "4px 0", color: "#16a34a" }}>
                    <strong>SCTS:</strong> ₹{scts.toLocaleString()}
                  </p>

                  <p style={{ margin: "4px 0", color: "#dc2626" }}>
                    <strong>ETS:</strong> ₹{ets.toLocaleString()}
                  </p>
                </div>
              );
            }}
          />

          <Area
            type="monotone"
            dataKey="total"
            stroke="#2563eb"
            strokeWidth={3}
            fill="url(#salesGradient)"
            activeDot={{ r: 6 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RevenueTrend;
